"use strict";

// Unicode simple case folding equivalence classes that are not fully
// derivable from {toLowerCase, toUpperCase} minus self: classes with three
// or more members like {K, k, Kelvin sign}, pairs joined by folding only
// like {ᾀ, ᾈ}, and the singleton dotless i class, which maps to I through
// toUpperCase but folds only to itself.
// Generated by scripts/regex_case_folding/generate_overrides.mjs.
const OVERRIDE_FOLDING_CLASSES = [
  [0x004b, 0x006b, 0x212a], // K k K
  [0x0053, 0x0073, 0x017f], // S s ſ
  [0x00b5, 0x039c, 0x03bc], // µ Μ μ
  [0x00c5, 0x00e5, 0x212b], // Å å Å
  [0x00df, 0x1e9e], // ß ẞ
  [0x0131], // ı
  [0x01c4, 0x01c5, 0x01c6], // Ǆ ǅ ǆ
  [0x01c7, 0x01c8, 0x01c9], // Ǉ ǈ ǉ
  [0x01ca, 0x01cb, 0x01cc], // Ǌ ǋ ǌ
  [0x01f1, 0x01f2, 0x01f3], // Ǳ ǲ ǳ
  [0x0345, 0x0399, 0x03b9, 0x1fbe], // combining iota, Ι ι ι
  [0x0392, 0x03b2, 0x03d0], // Β β ϐ
  [0x0395, 0x03b5, 0x03f5], // Ε ε ϵ
  [0x0398, 0x03b8, 0x03d1, 0x03f4], // Θ θ ϑ ϴ
  [0x039a, 0x03ba, 0x03f0], // Κ κ ϰ
  [0x03a0, 0x03c0, 0x03d6], // Π π ϖ
  [0x03a1, 0x03c1, 0x03f1], // Ρ ρ ϱ
  [0x03a3, 0x03c2, 0x03c3], // Σ ς σ
  [0x03a6, 0x03c6, 0x03d5], // Φ φ ϕ
  [0x03a9, 0x03c9, 0x2126], // Ω ω Ω
  [0x0412, 0x0432, 0x1c80], // В в ᲀ
  [0x0414, 0x0434, 0x1c81], // Д д ᲁ
  [0x041e, 0x043e, 0x1c82], // О о ᲂ
  [0x0421, 0x0441, 0x1c83], // С с ᲃ
  [0x0422, 0x0442, 0x1c84, 0x1c85], // Т т ᲄ ᲅ
  [0x042a, 0x044a, 0x1c86], // Ъ ъ ᲆ
  [0x0462, 0x0463, 0x1c87], // Ѣ ѣ ᲇ
  [0x1c88, 0xa64a, 0xa64b], // ᲈ Ꙋ ꙋ
  [0x1e60, 0x1e61, 0x1e9b], // Ṡ ṡ ẛ
  [0x1f80, 0x1f88], // ᾀ ᾈ
  [0x1f81, 0x1f89], // ᾁ ᾉ
  [0x1f82, 0x1f8a], // ᾂ ᾊ
  [0x1f83, 0x1f8b], // ᾃ ᾋ
  [0x1f84, 0x1f8c], // ᾄ ᾌ
  [0x1f85, 0x1f8d], // ᾅ ᾍ
  [0x1f86, 0x1f8e], // ᾆ ᾎ
  [0x1f87, 0x1f8f], // ᾇ ᾏ
  [0x1f90, 0x1f98], // ᾐ ᾘ
  [0x1f91, 0x1f99], // ᾑ ᾙ
  [0x1f92, 0x1f9a], // ᾒ ᾚ
  [0x1f93, 0x1f9b], // ᾓ ᾛ
  [0x1f94, 0x1f9c], // ᾔ ᾜ
  [0x1f95, 0x1f9d], // ᾕ ᾝ
  [0x1f96, 0x1f9e], // ᾖ ᾞ
  [0x1f97, 0x1f9f], // ᾗ ᾟ
  [0x1fa0, 0x1fa8], // ᾠ ᾨ
  [0x1fa1, 0x1fa9], // ᾡ ᾩ
  [0x1fa2, 0x1faa], // ᾢ ᾪ
  [0x1fa3, 0x1fab], // ᾣ ᾫ
  [0x1fa4, 0x1fac], // ᾤ ᾬ
  [0x1fa5, 0x1fad], // ᾥ ᾭ
  [0x1fa6, 0x1fae], // ᾦ ᾮ
  [0x1fa7, 0x1faf], // ᾧ ᾯ
  [0x1fb3, 0x1fbc], // ᾳ ᾼ
  [0x1fc3, 0x1fcc], // ῃ ῌ
  [0x1ff3, 0x1ffc], // ῳ ῼ
];

// Per-member variant lists derived from the override classes.
const CASE_VARIANT_OVERRIDES = new Map();

for (const foldingClass of OVERRIDE_FOLDING_CLASSES) {
  for (const codePoint of foldingClass) {
    CASE_VARIANT_OVERRIDES.set(
      codePoint,
      foldingClass.filter((member) => member !== codePoint),
    );
  }
}

// Returns the code points that match the given code point caselessly under
// Unicode simple case folding, excluding the code point itself.
export function caseVariants(codePoint) {
  const overridden = CASE_VARIANT_OVERRIDES.get(codePoint);

  if (overridden !== undefined) return overridden;

  const char = String.fromCodePoint(codePoint);
  const variants = [];

  for (const mapped of [char.toLowerCase(), char.toUpperCase()]) {
    const mappedCodePoint = mapped.codePointAt(0);

    if (
      mapped.length === String.fromCodePoint(mappedCodePoint).length &&
      mappedCodePoint !== codePoint
    ) {
      variants.push(mappedCodePoint);
    }
  }

  return variants;
}
